import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { GALLERY_ITEMS } from '../mock';

export default function Auth({ mode = 'login' }) {
  const isRegister = mode === 'register';
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || (isRegister && !form.name)) {
      toast({ title: 'Missing details', description: 'Please fill in all fields to continue.' });
      return;
    }
    setBusy(true);
    try {
      if (isRegister) {
        await register(form.name, form.email, form.password);
        toast({ title: 'Welcome to Idolaris', description: 'Your studio is ready with 150 free credits.' });
      } else {
        await login(form.email, form.password);
        toast({ title: 'Welcome back', description: 'Signed in successfully.' });
      }
      navigate('/app');
    } catch (err) {
      toast({ title: 'Authentication failed', description: err.message || 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  const heroImg = GALLERY_ITEMS[3].src;

  return (
    <div className="min-h-screen bg-background text-foreground lg:grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex min-h-screen flex-col px-5 py-8 sm:px-10 lg:px-16">
        <Link to="/" className="flex items-center gap-2">
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none">
            <circle cx="5" cy="6" r="2.4" fill="#F5A623" />
            <circle cx="5" cy="18" r="2.4" fill="#A78BFA" />
            <circle cx="19" cy="12" r="2.4" fill="#fff" />
            <path d="M7 6.6 17 11.4M7 17.4 17 12.6" stroke="#3A3A42" strokeWidth="1.3" />
          </svg>
          <span className="font-serif-display text-xl text-white">Idolaris</span>
        </Link>

        <div className="flex flex-1 items-center">
          <div className="mx-auto w-full max-w-sm">
            <h1 className="font-serif-display text-3xl text-white sm:text-4xl">
              {isRegister ? 'Create your studio' : 'Welcome back'}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {isRegister ? 'Start with 150 free credits. No card required.' : 'Sign in to keep directing.'}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white/80">Name</Label>
                  <Input id="name" placeholder="Ada Lovelace" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="h-11 border-border bg-card text-white placeholder:text-muted-foreground focus-visible:ring-node-style" />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80">Email</Label>
                <Input id="email" type="email" placeholder="you@studio.com" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-11 border-border bg-card text-white placeholder:text-muted-foreground focus-visible:ring-node-style" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80">Password</Label>
                <Input id="password" type="password" placeholder="••••••••" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="h-11 border-border bg-card text-white placeholder:text-muted-foreground focus-visible:ring-node-style" />
              </div>

              <Button type="submit" disabled={busy} className="group h-11 w-full rounded-full bg-white text-sm font-medium text-black hover:bg-white/90 disabled:opacity-60">
                {busy ? 'Please wait…' : (isRegister ? 'Create account' : 'Sign in')}
                <ArrowRight size={16} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
              </Button>
              {/* Someone who mistyped their password on signup has paid
                  for something they cannot open, so the way back is on
                  the form rather than in a support email. */}
              {!isRegister && (
                <p className="mt-3 text-center text-xs text-white/40">
                  <Link to="/reset" className="hover:text-white">Forgot your password?</Link>
                </p>
              )}
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {isRegister ? 'Already have an account? ' : "Don't have an account? "}
              <Link to={isRegister ? '/login' : '/register'} className="text-white underline-offset-4 hover:underline">
                {isRegister ? 'Log in' : 'Sign up for free'}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right: visual */}
      <div className="relative hidden overflow-hidden lg:block">
        <img src={heroImg} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-12">
          <p className="font-serif-display text-2xl leading-snug text-white">
            “A product of art direction that looks ugly doesn&apos;t sell.”
          </p>
          <p className="mt-3 text-sm text-white/60">Idolaris, the visual AI studio</p>
        </div>
      </div>
    </div>
  );
}
